import curry from "../../../../lib/core/functional/curry";
import pipe from "../../../../lib/core/functional/pipe";

import {
    getSuccessMessageTemplate,
    getFeedbackTemplate,
    getGotoTemplate,
    getSubmitTemplate,
    getLabeledInputField,
    getPrivacyPolicyTemplate,
    getExtraFieldTemplate,
    getSubmitOtpTemplate,
    getFeedbackOtpTemplate,
} from "../templates";

import { validatePasswordConfirmation, addSubmitValidator } from "./validators";

import {
    getValuesFromModule,
    cleanElements,
    displayEventOnElement,
    toggleSiblingsOf,
    setElementAttribute,
} from "./utils";

function renderPasswordField(form, params, siblings) {
    if (!params.autogenerate_password && params.password)
        return [
            ...siblings,
            getLabeledInputField({
                type: "password",
                name: "password",
                label: params.password_label || "Enter your password:",
                required: true,
            }),
        ];
    else return siblings;
}

function renderPasswordConfirmField(form, params, siblings) {
    if (!params.autogenerate_password && params.password && params.confirm_password) {
        addSubmitValidator(form, validatePasswordConfirmation);

        return [
            ...siblings,
            getLabeledInputField({
                type: "password",
                name: "confirm_password",
                label: params.confirm_password_label || "Confirm you password:",
                required: true,
            }),
        ];
    } else return siblings;
}

const renderPasswordOn = curry(renderPasswordField);

const renderConfirmPasswordOn = curry(renderPasswordConfirmField);

function handleCurriedRenderer(form, params) {
    return function applyContextOnRenderer(renderer) {
        return renderer(form, params);
    };
}

export const otp = function otpModule(form, wrapper, params = {}){
    wrapper.classList.remove("authentication--loading");
    const input = getLabeledInputField({
        type: "text",
        name: "otp",
        label: "Enter the OTP code:",
        required: true,
    });
    const button = getSubmitOtpTemplate(params);

    wrapper.innerHTML =  input + button;

    form.addEventListener("mfa:enabled", toggleSiblingsOf(form, ".otp-form", true), true);
}

export const otpFeedback = function otpFeedbackModule(form, wrapper, params={}){
    wrapper.classList.remove("authentication--loading");

    wrapper.innerHTML = getFeedbackOtpTemplate();

    const successContainer = wrapper.querySelector(".otp--success-feedback");
    const errorContainer = wrapper.querySelector(".otp--error-feedback");

    successContainer.classList.add("hidden");
    errorContainer.classList.add("hidden");

    form.addEventListener("otp:valid", toggleSiblingsOf(successContainer, ".otp--success-feedback", true));
    form.addEventListener("otp:invalid", toggleSiblingsOf(errorContainer, ".otp--error-feedback", true));
}

export const authorization = function authorizationModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    const emailField = getLabeledInputField({
        type: "email",
        name: "email",
        label: params.email_label || "Enter your email:",
        required: true,
    });

    const pieceRenderers = [renderPasswordOn, renderConfirmPasswordOn].map(
        handleCurriedRenderer(form, params)
    );

    wrapper.innerHTML = pipe(...pieceRenderers)([emailField]).join("");
    return getValuesFromModule("authorization", wrapper);
};

export const extraField = function customFieldModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getExtraFieldTemplate(params);
    return getValuesFromModule("extra_field", wrapper);
};

export const feedback = function feedbackModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getFeedbackTemplate();
    const successContainer = wrapper.querySelector(".authentication--success-feedback");
    const errorContainer = wrapper.querySelector(".authentication--error-feedback");

    form.addEventListener("submit", cleanElements(successContainer, errorContainer));
    form.addEventListener("authentication:success", displayEventOnElement(successContainer));
    form.addEventListener("authentication:error", displayEventOnElement(errorContainer));
};

export const goto = function gotoModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getGotoTemplate(params);
    const gotoElement = wrapper.querySelector(".authentication--goto");
    gotoElement.addEventListener("click", toggleSiblingsOf(form, params.to, true), true);
};

export const privacyPolicy = function privacyPolicyModule(form, wrapper, params) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getPrivacyPolicyTemplate(params);
    return getValuesFromModule(null, wrapper);
};

function formActionReloadFactory(form) {
    return function onSubmitSuccess(_event) {
        form.action = window.location.href;
    };
}

export const submit = function submitModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getSubmitTemplate(params);

    if (params.reload) {
        form.action = window.location.href;
        form.addEventListener("authentication:success", formActionReloadFactory(form));
    } else if (params.redirect) {
        form.action = params.redirect;
    } else if (params.soft_reload) {
        form.action = "#";
    }

    const submitButton = wrapper.querySelector("button");

    const disableButtonOnSubmit = setElementAttribute(submitButton, "disabled", true);
    const enableButtonAfterSubmit = setElementAttribute(submitButton, "disabled", false);

    form.addEventListener("submit", disableButtonOnSubmit);
    form.addEventListener("authentication:success", enableButtonAfterSubmit);
    form.addEventListener("authentication:error", enableButtonAfterSubmit);
};

function successMessageEventFactory(wrapper, scrollTo) {
    return function onSuccessMessage(event) {
        toggleSiblingsOf(
            wrapper,
            ".authentication--success_message-module, .authentication--goto-module",
            scrollTo,
            event
        );

        try {
            localStorage.setItem(wrapper.className, true);
        } catch (e) {
            console.log(e);
        }
    };
}

export const successMessage = function successMessageModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML = getSuccessMessageTemplate(params);

    if (params.persistant && localStorage.getItem(wrapper.className))
        form.addEventListener(
            "authentication:rendered",
            successMessageEventFactory(wrapper, false)
        );

    wrapper.classList.add("hidden");

    form.addEventListener("authentication:success", successMessageEventFactory(wrapper, true));
};

export const userInfo = function userInfoModule(form, wrapper, params = {}) {
    wrapper.classList.remove("authentication--loading");
    wrapper.innerHTML =
        getLabeledInputField({
            type: "text",
            name: "first_name",
            label: params.first_name_label || "First Name:",
            required: params.required,
        }) +
        getLabeledInputField({
            type: "text",
            name: "last_name",
            label: params.last_name_label || "Last Name:",
            required: params.required,
        });

    return getValuesFromModule("user_info", wrapper);
};

export const afterSignup = function afterSignupModule(form, wrapper, params) {
    const action = params.action;
    const defaultValue = params.value;

    wrapper.classList.add("hidden");

    return function getAfterSignupValues(store) {
        let storeAcum = store;

        if (!("after_signup" in store)) store.after_signup = {};
        storeAcum = store.after_signup;

        storeAcum[action] = window[`rm-after-signup-${action}`] || defaultValue;
        return store;
    };
};
